import ListingGridTile from '@/components/cards/ListingGridTile'
import EmptyState from '@/components/ui/EmptyState'
import GameBadge from '@/components/ui/GameBadge'
import SocialLinks from '@/components/profile/SocialLinks'
import { useAuthModal } from '@/contexts/AuthModalContext'
import { useAuth } from '@/hooks/useAuth'
import type { Card } from '@/hooks/useCards'
import { useCards } from '@/hooks/useCards'
import { useStoreBySlug } from '@/hooks/useProfile'
import { isAuctionListing } from '@/lib/games'
import { messageThreadPath } from '@/lib/messages'
import { getStoreUrlPreview } from '@/lib/storeSlug'
import { getStoreLayout, getStoreTheme } from '@/lib/storeTheme'
import {ExternalLink, Grid3X3, Mail, Store as StoreIcon, X} from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

function Store() {
  const { storeSlug, userId } = useParams()
  const { profile, loading } = useStoreBySlug(storeSlug || userId)
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const navigate = useNavigate()
  const { cards, loading: cardsLoading } = useCards(profile?.userId)
  const [activeCard, setActiveCard] = useState<Card | null>(null)
  const isOwner = Boolean(user?.userId && profile?.userId && user.userId === profile.userId)
  const layout = getStoreLayout(profile?.storeLayout)
  const theme = getStoreTheme(profile?.colorScheme)
  const isCentered = layout === 'centered'
  const isMagazine = layout === 'magazine'
  const isGallery = layout === 'gallery'
  const gridClass = isMagazine
    ? 'grid grid-cols-2 gap-3 px-4 sm:px-6'
    : isGallery
      ? 'grid grid-cols-2 gap-3 px-4 sm:grid-cols-3 sm:px-6'
      : isCentered
        ? 'grid grid-cols-2 gap-2 px-4 sm:grid-cols-3 sm:px-6'
        : 'grid grid-cols-3 gap-0.5 sm:gap-1'

  if (loading) {
    return (
      <div className="mx-auto max-w-[935px] px-0 pb-8 sm:px-4 sm:pt-6">
        <div className="h-44 animate-pulse bg-white/5 sm:rounded-3xl" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <EmptyState
          icon={StoreIcon}
          title="Store not found"
          description="This shop name is not live yet. Check the link or browse the feed for open stores."
        />
      </div>
    )
  }

  return (
    <div
      className="relative mx-auto min-h-[calc(100vh-8rem)] w-full max-w-[1100px] overflow-hidden pb-16 sm:rounded-[32px] sm:pb-10 sm:pt-4"
      style={{ backgroundColor: theme.page }}
    >
      {profile.backgroundUrl ? (
        <div className="pointer-events-none absolute inset-0">
          <img src={profile.backgroundUrl} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: theme.overlay }} />
        </div>
      ) : (
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background: `radial-gradient(circle at top left, ${theme.primary}33, transparent 36%), radial-gradient(circle at bottom right, ${theme.secondary}33, transparent 32%)`,
          }}
        />
      )}

      <div className="relative">
        <div className={`relative w-full ${isMagazine ? 'h-56 sm:h-72' : isGallery ? 'h-36 sm:h-48' : 'h-44 sm:h-60'} ${isCentered || isMagazine ? 'sm:rounded-[28px]' : 'sm:rounded-t-3xl'}`}>
          <div className="absolute inset-0 overflow-hidden bg-transparent sm:rounded-[inherit]">
            {profile.bannerUrl ? (
              <>
                <img src={profile.bannerUrl} alt="" className="h-full w-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/10" />
              </>
            ) : null}
          </div>
          <div
            className={`absolute z-10 overflow-hidden rounded-full border-4 bg-[#111111] shadow-[0_8px_24px_rgba(0,0,0,0.45)] ${
              isCentered
                ? 'bottom-[-42px] left-1/2 h-28 w-28 -translate-x-1/2 sm:bottom-[-48px] sm:h-32 sm:w-32'
                : isGallery
                  ? 'bottom-3 left-4 h-20 w-20 sm:bottom-4 sm:left-6 sm:h-24 sm:w-24'
                  : 'bottom-3 left-4 h-24 w-24 sm:bottom-4 sm:left-6 sm:h-32 sm:w-32'
            }`}
            style={{ borderColor: theme.accent }}
          >
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={profile.storeName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-3xl font-extrabold text-white" style={{ backgroundColor: theme.primary }}>
                {(profile.storeName || '?')[0]?.toUpperCase()}
              </div>
            )}
          </div>
        </div>

        <div className={`px-4 sm:px-6 ${isCentered ? 'mt-14 text-center' : 'mt-4'}`}>
          <div className={`flex min-w-0 flex-col gap-3 ${isCentered ? 'items-center' : 'sm:flex-row sm:items-start sm:justify-between'}`}>
            <div className="min-w-0">
              <h1 className="font-display break-words text-2xl font-extrabold leading-tight text-white sm:text-3xl">
                {profile.storeName}
              </h1>
              <p className="mt-1 break-all text-xs font-extrabold" style={{ color: theme.accent }}>
                {getStoreUrlPreview(profile.storeName, profile.storeSlug)}
              </p>
            </div>
            <div className={`flex shrink-0 flex-wrap gap-2 ${isCentered ? 'justify-center' : 'self-start'}`}>
              {isOwner ? (
                <Link
                  to="/dashboard"
                  className="rounded-lg border px-3 py-1.5 text-xs font-extrabold text-white hover:bg-white/15"
                  style={{ borderColor: `${theme.accent}66`, backgroundColor: `${theme.surface}cc` }}
                >
                  Edit profile
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!profile.userId) return
                    if (!isAuthenticated) {
                      openAuth()
                      return
                    }
                    navigate(messageThreadPath(profile.userId))
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-extrabold text-black"
                  style={{ backgroundColor: theme.accent, color: theme.accentInk }}
                >
                  <Mail className="h-3.5 w-3.5" />
                  Message
                </button>
              )}
            </div>
          </div>

          <div className={`mt-5 flex items-center gap-6 border-y py-3 text-sm ${isCentered ? 'justify-center' : ''}`} style={{ borderColor: `${theme.accent}22` }}>
            <div>
              <span className="font-extrabold text-white">{cards.length}</span>
              <span className="ml-1 font-semibold text-white/50">{cards.length === 1 ? 'listing' : 'listings'}</span>
            </div>
          </div>

          <section className={`mt-5 ${isCentered ? 'mx-auto max-w-2xl' : ''} ${isMagazine ? 'rounded-3xl border p-4' : ''}`} style={isMagazine ? { backgroundColor: `${theme.surface}cc`, borderColor: `${theme.secondary}66` } : undefined}>
            <h2 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/40">Bio</h2>
            {profile.bio ? (
              <p className="mt-2 max-w-xl break-words text-sm font-semibold leading-relaxed text-white/80">
                {profile.bio}
              </p>
            ) : (
              <p className="mt-2 text-sm font-semibold italic text-white/35">
                {isOwner ? 'Add a bio so collectors know what you sell.' : 'This shop has not added a bio yet.'}
              </p>
            )}
          </section>

          {profile.favoriteGames?.length ? (
            <div className={`mt-4 flex flex-wrap gap-1.5 ${isCentered ? 'justify-center' : ''}`}>
              {profile.favoriteGames.map(game => (
                <span
                  key={game}
                  className="rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide"
                  style={{ borderColor: `${theme.accent}59`, backgroundColor: `${theme.accent}1a`, color: theme.accent }}
                >
                  {game}
                </span>
              ))}
            </div>
          ) : null}

          <div className={`mt-4 flex flex-wrap items-center gap-2.5 ${isCentered ? 'justify-center' : ''}`}>
            <SocialLinks
              links={{
                tiktokUrl: profile.tiktokUrl,
                xUrl: profile.xUrl,
                facebookUrl: profile.facebookUrl,
                instagramUrl: profile.instagramUrl,
                websiteUrl: profile.websiteUrl,
              }}
            />
            {profile.tcgplayerStoreUrl ? (
              <a
                href={profile.tcgplayerStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-extrabold text-white"
                style={{ borderColor: theme.secondary, backgroundColor: `${theme.secondary}33` }}
              >
                TCGplayer
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
            {profile.ebayStoreUrl ? (
              <a
                href={profile.ebayStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-extrabold"
                style={{ borderColor: `${theme.accent}80`, backgroundColor: `${theme.accent}1a`, color: theme.accent }}
              >
                eBay
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>

        <div className="relative mt-6 flex items-center justify-center gap-2 border-t py-3 text-[11px] font-extrabold uppercase tracking-[0.2em] text-white/50" style={{ borderColor: `${theme.accent}22` }}>
          <Grid3X3 className="h-4 w-4" />
          Listings
        </div>

        {cardsLoading ? (
          <div className={gridClass}>
            {Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="aspect-square animate-pulse bg-white/5" />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div className="px-4 py-6 pb-10 sm:py-10">
            <EmptyState
              icon={Grid3X3}
              title="No listings yet"
              description="This shop has not posted cards or sealed product yet."
            />
          </div>
        ) : (
          <div className={gridClass}>
            {cards.map(card => (
              <ListingGridTile key={card._id} card={card} onOpen={() => setActiveCard(card)} />
            ))}
          </div>
        )}
      </div>

      {activeCard ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/85 px-3 py-4 sm:items-center sm:px-4 sm:py-8"
          onClick={() => setActiveCard(null)}
        >
          <div
            className="relative mb-16 w-full max-w-3xl overflow-hidden rounded-3xl border shadow-2xl sm:mb-0"
            style={{ backgroundColor: theme.surface, borderColor: `${theme.secondary}66` }}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveCard(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-2 text-white/70 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="grid max-h-[calc(100dvh-7rem)] overflow-y-auto md:max-h-none md:grid-cols-[1.1fr_0.9fr]">
              <div className="aspect-square bg-black">
                {activeCard.imageUrl ? (
                  <img src={activeCard.imageUrl} alt={activeCard.title} className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="flex flex-col gap-3 p-4 sm:p-5">
                <GameBadge listingType={activeCard.listingType} productType={activeCard.productType} />
                <h2 className="font-display break-words text-xl font-extrabold text-white sm:text-2xl">{activeCard.title}</h2>
                {isAuctionListing(activeCard.saleType) ? (
                  <span className="w-fit rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white" style={{ backgroundColor: theme.primary }}>Auction</span>
                ) : activeCard.price != null ? (
                  <span className="w-fit rounded-full px-3 py-1 text-sm font-extrabold" style={{ backgroundColor: theme.accent, color: theme.accentInk }}>${Number(activeCard.price).toFixed(2)}</span>
                ) : null}
                {activeCard.condition ? <p className="text-xs font-extrabold uppercase tracking-wide text-white/40">{activeCard.condition}</p> : null}
                {activeCard.description ? <p className="break-words text-sm font-semibold text-white/65">{activeCard.description}</p> : null}
                <div className="mt-auto flex flex-wrap gap-2 pt-2">
                  {!isOwner && profile.userId ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!isAuthenticated) {
                          openAuth()
                          return
                        }
                        navigate(messageThreadPath(profile.userId))
                      }}
                      className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-extrabold"
                      style={{ backgroundColor: theme.accent, color: theme.accentInk }}
                    >
                      <Mail className="h-3 w-3" />
                      Message seller
                    </button>
                  ) : null}
                  {activeCard.tcgplayerUrl ? (
                    <a href={activeCard.tcgplayerUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-extrabold text-white" style={{ borderColor: theme.secondary, backgroundColor: `${theme.secondary}33` }}>
                      TCGplayer <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                  {activeCard.ebayUrl ? (
                    <a href={activeCard.ebayUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-extrabold" style={{ borderColor: `${theme.accent}80`, backgroundColor: `${theme.accent}1a`, color: theme.accent }}>
                      eBay <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default Store
